import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Container, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SignupProvider, useSignup } from './SignupContext';
import { SIGNUP_STEPS, type SignupStepId, type SignupTier, type SignupBilling } from './types';
// StepRuolo non piu' usato dal wizard (refactor 2026-05-18, signup generico).
// Il file resta su disco per eventuale riuso futuro come opzionale.
import StepTier from './steps/StepTier';
import StepDatiAzienda from './steps/StepDatiAzienda';
import StepProfilo from './steps/StepProfilo';
import StepTerminiPrivacy from './steps/StepTerminiPrivacy';
import StepRiepilogo from './steps/StepRiepilogo';
import { completeMarketplaceSignup } from '../../services/SignupService';
import { getMarketplaceAuth } from '../../firebase';

const VALID_TIERS: SignupTier[] = ['free', 'base', 'pro', 'business'];
const VALID_BILLING: SignupBilling[] = ['monthly', 'yearly'];

function canAdvance(
  stepId: SignupStepId,
  state: ReturnType<typeof useSignup>['state'],
): boolean {
  switch (stepId) {
    case 'tier':
      return !!(state.tier && state.billing_frequency);
    case 'azienda':
      return !!(
        state.ragione_sociale &&
        state.partita_iva &&
        state.sede_via &&
        state.sede_citta &&
        state.sede_cap &&
        state.sede_provincia &&
        state.sede_regione
      );
    case 'profilo': {
      const auth = getMarketplaceAuth();
      const currentUser = auth.currentUser;
      const isGoogle = !!currentUser?.providerData?.some((p) => p.providerId === 'google.com');
      if (isGoogle) {
        // Google: serve solo telefono (gli altri sono dal provider)
        return !!state.admin_telefono;
      }
      // Email/password: serve tutto
      return !!(
        state.admin_nome &&
        state.admin_cognome &&
        state.admin_email &&
        state.admin_telefono &&
        state.admin_password &&
        state.admin_password.length >= 10
      );
    }
    case 'termini':
      return !!(state.accettato_termini && state.accettato_privacy);
    case 'riepilogo':
      return true;
  }
}

function Stepper({ currentStep }: { currentStep: number }) {
  const { t } = useTranslation('signup');
  const currentStepDef = SIGNUP_STEPS[currentStep];

  return (
    <>
      {/* Mobile (xs): compressed single-line stepper "01 / 05 · Azienda" */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'baseline',
          gap: 1.5,
          py: 0.5,
          width: '100%',
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono Variable", monospace',
            fontSize: '0.65rem',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--sun-deep)',
            flexShrink: 0,
          }}
        >
          0{currentStep + 1} / 0{SIGNUP_STEPS.length}
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Fraunces Variable", Georgia, serif',
            fontSize: '1.1rem',
            fontWeight: 500,
            color: 'var(--ink)',
            letterSpacing: '-0.01em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {t(`stepper.${currentStepDef.id}`)}
        </Typography>
      </Box>

      {/* Desktop (md+): full multi-step horizontal stepper */}
      <Stack
        direction="row"
        spacing={0}
        sx={{ width: '100%', display: { xs: 'none', md: 'flex' } }}
      >
        {SIGNUP_STEPS.map((s, i) => {
          const isActive = i === currentStep;
          const isPast = i < currentStep;
          return (
            <Box
              key={s.id}
              sx={{
                flex: 1,
                borderRight:
                  i < SIGNUP_STEPS.length - 1 ? '1px solid var(--dust)' : 'none',
                px: 2,
                opacity: isActive ? 1 : isPast ? 0.6 : 0.35,
                transition: 'opacity 240ms',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono Variable", monospace',
                  fontSize: '0.65rem',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: isActive ? 'var(--sun-deep)' : 'var(--ink-soft)',
                  mb: 0.3,
                }}
              >
                0{i + 1} {' '}/ {' '}{SIGNUP_STEPS.length}
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Fraunces Variable", Georgia, serif',
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  color: 'var(--ink)',
                  letterSpacing: '-0.01em',
                }}
              >
                {t(`stepper.${s.id}`)}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </>
  );
}

function WizardInner() {
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { state, update } = useSignup();
  const { t } = useTranslation('signup');
  const { t: tCommon } = useTranslation('common');
  const [searchParams] = useSearchParams();

  /**
   * Smart Step 0: se l'utente arriva dal marketing-site con ?tier=X&billing=Y
   * preselezionati, skippa lo step "tier" automaticamente e parti da Step 1.
   * Esempi: iscriviti.yumaos.com?tier=pro → tier preselezionato, parte da azienda.
   *         iscriviti.yumaos.com           → mostra Step 0 (scelta tier).
   */
  useEffect(() => {
    const tierParam = searchParams.get('tier') as SignupTier | null;
    const billingParam = searchParams.get('billing') as SignupBilling | null;

    const isValidTier = tierParam && VALID_TIERS.includes(tierParam);
    const isValidBilling = billingParam && VALID_BILLING.includes(billingParam);

    if (isValidTier) {
      update({
        tier: tierParam,
        billing_frequency: isValidBilling ? billingParam : 'monthly',
        tier_preselected: true,
      });
      // Salta lo Step 0 (tier), inizia da Step 1 (azienda)
      setCurrentStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Layout dimensions used to offset the scrollable content
  // Match the actual fixed-bar heights so content does not hide under them
  const HEADER_H_XS = 56;
  const HEADER_H_MD = 64;
  const STEPPER_H_XS = 56;
  const STEPPER_H_MD = 64;
  const BOTTOM_H_XS = 72;
  const BOTTOM_H_MD = 80;

  const stepDef = SIGNUP_STEPS[currentStep];
  const isLast = currentStep === SIGNUP_STEPS.length - 1;

  const renderStep = () => {
    switch (stepDef.id) {
      case 'tier':
        return <StepTier />;
      case 'azienda':
        return <StepDatiAzienda />;
      case 'profilo':
        return <StepProfilo />;
      case 'termini':
        return <StepTerminiPrivacy />;
      case 'riepilogo':
        return <StepRiepilogo />;
    }
  };

  const handleNext = async () => {
    if (isLast) {
      setSubmitting(true);
      setSubmitError(null);
      try {
        // Determina signup_method dal Firebase Auth currentUser providerData
        const auth = getMarketplaceAuth();
        const currentUser = auth.currentUser;
        const isGoogle = currentUser?.providerData?.some((p) => p.providerId === 'google.com');
        const signupMethod: 'email_password' | 'google' = isGoogle ? 'google' : 'email_password';

        const result = await completeMarketplaceSignup(state, signupMethod, 'it');
        if (result.ok) {
          navigate('/iscriviti/verifica-email', {
            state: {
              email: result.email,
              ragione_sociale: state.ragione_sociale,
              app_login_url: result.app_login_url,
              email_verified: result.email_verified,
              signup_method: signupMethod,
            },
          });
        } else {
          setSubmitError(result.error_code);
        }
      } catch (err) {
        console.error('[Wizard] submit crashed', err);
        setSubmitError('register.unknown_error');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setCurrentStep((c) => c + 1);
  };

  const handleBack = () => {
    setCurrentStep((c) => Math.max(0, c - 1));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'var(--bg)',
      }}
    >
      {/* Top bar */}
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 11,
          height: { xs: HEADER_H_XS, md: HEADER_H_MD },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: { xs: '0 24px', md: '0 56px' },
          background: 'rgba(244, 239, 230, 0.88)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderBottom: '1px solid var(--dust)',
        }}
      >
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono Variable", monospace',
            fontSize: '0.7rem',
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'var(--ink)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            component="a"
            href="/"
            sx={{
              color: 'inherit',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              '&:hover': { color: 'var(--sun-deep)' },
            }}
          >
            <Box
              component="span"
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: 'var(--sun)',
              }}
            />
            {tCommon('brand')}
          </Box>
        </Typography>

        <Typography
          sx={{
            display: { xs: 'none', sm: 'block' },
            fontFamily: '"JetBrains Mono Variable", monospace',
            fontSize: '0.7rem',
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'var(--ink-soft)',
          }}
        >
          {t('header.tag_iscrizione')}
        </Typography>
      </Box>

      {/* Stepper — fixed below header */}
      <Box
        sx={{
          position: 'fixed',
          top: { xs: HEADER_H_XS, md: HEADER_H_MD },
          left: 0,
          right: 0,
          zIndex: 10,
          height: { xs: STEPPER_H_XS, md: STEPPER_H_MD },
          background: 'rgba(244, 239, 230, 0.85)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderBottom: '1px solid var(--dust)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="md" sx={{ py: 0, width: '100%' }}>
          <Stepper currentStep={currentStep} />
        </Container>
      </Box>

      {/* Content */}
      <Container
        maxWidth="md"
        component="main"
        sx={{
          paddingTop: {
            xs: `${HEADER_H_XS + STEPPER_H_XS + 32}px`,
            md: `${HEADER_H_MD + STEPPER_H_MD + 48}px`,
          },
          paddingBottom: {
            xs: `${BOTTOM_H_XS + 32}px`,
            md: `${BOTTOM_H_MD + 48}px`,
          },
        }}
      >
        <Box sx={{ minHeight: 460 }}>{renderStep()}</Box>

        {submitError && (
          <Box
            sx={{
              mt: 4,
              padding: '16px 20px',
              border: '1px solid #B33A2E',
              borderLeft: '3px solid #B33A2E',
              background: 'rgba(179, 58, 46, 0.05)',
            }}
          >
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono Variable", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#B33A2E',
                mb: 0.5,
              }}
            >
              {t('errors.errore_titolo')}
            </Typography>
            <Typography sx={{ color: 'var(--ink)', fontSize: '0.95rem' }}>
              {t(`errors.${submitError}`, {
                defaultValue: t('errors.signup.unknown_error') as string,
              })}
            </Typography>
          </Box>
        )}
      </Container>

      {/* Fixed bottom action bar */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          height: { xs: BOTTOM_H_XS, md: BOTTOM_H_MD },
          background: 'rgba(244, 239, 230, 0.92)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderTop: '1px solid var(--dust)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="md" sx={{ py: 0, width: '100%' }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Button
              variant="text"
              disabled={currentStep === 0}
              onClick={handleBack}
              sx={{
                fontFamily: '"JetBrains Mono Variable", monospace',
                fontSize: { xs: '0.72rem', md: '0.78rem' },
                letterSpacing: '0.12em',
                color: 'var(--ink)',
                minHeight: 44,
                minWidth: 44,
                padding: { xs: '10px 8px', md: '10px 12px' },
                '&:hover': {
                  background: 'transparent',
                  color: 'var(--sun-deep)',
                },
                '&.Mui-disabled': { color: 'var(--dust)' },
              }}
            >
              ← {tCommon('indietro')}
            </Button>

            {/* Centered mini-indicator: step current / total */}
            <Typography
              sx={{
                display: { xs: 'none', md: 'block' },
                fontFamily: '"JetBrains Mono Variable", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--ink-soft)',
              }}
            >
              {t('stepper.step_di', {
                current: `0${currentStep + 1}`,
                total: `0${SIGNUP_STEPS.length}`,
              })}
            </Typography>

            <Button
              variant="contained"
              disabled={!canAdvance(stepDef.id, state) || submitting}
              onClick={handleNext}
              sx={{
                background: 'var(--ink)',
                color: 'var(--bg)',
                fontSize: { xs: '0.74rem', md: '0.8rem' },
                fontFamily: '"JetBrains Mono Variable", monospace',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                padding: { xs: '12px 16px', md: '14px 24px' },
                minHeight: 44,
                borderRadius: 0,
                transition: 'all 240ms cubic-bezier(0.2, 0, 0.2, 1)',
                '& .arrow': {
                  display: 'inline-block',
                  marginLeft: '12px',
                  transition: 'transform 240ms',
                },
                '&:hover': {
                  background: 'var(--sun-deep)',
                  '& .arrow': { transform: 'translateX(8px)' },
                },
                '&.Mui-disabled': {
                  background: 'var(--dust)',
                  color: 'var(--bg-deep)',
                },
              }}
            >
              {submitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} />
                  <span>{t('submitting')}</span>
                </Box>
              ) : (
                <>
                  <span>{isLast ? tCommon('conferma_iscriviti') : tCommon('avanti')}</span>
                  <span className="arrow">→</span>
                </>
              )}
            </Button>
          </Stack>
        </Container>
      </Box>

    </Box>
  );
}

export default function SignupWizardPage() {
  return (
    <SignupProvider>
      <WizardInner />
    </SignupProvider>
  );
}
